"use client";

import React, { useState, useEffect, useContext } from "react";
import {
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "components/ui/table";

import DataTablePagination from "./data-table-pagination";
import DataTableToolbar from "./data-table-toolbar";
import { AppContext } from "contexts/AppContext";
import { formatDate } from "utils/helperFunctions";

function DataTable({ 
  columns, 
  data, 
  hideToolbar = false, 
  onTableReady = () => {}, 
  initialState = {} 
}) {
  const { dateRange, loading, setSelectedUser, onRowClick } =
    useContext(AppContext);

  const [rowSelection, setRowSelection] = useState(initialState.rowSelection || {});
  const [columnVisibility, setColumnVisibility] = useState(initialState.columnVisibility || {});
  const [columnFilters, setColumnFilters] = useState(initialState.columnFilters || []);
  const [globalFilter, setGlobalFilter] = useState(initialState.globalFilter || "");
  const [sorting, setSorting] = useState(initialState.sorting || []);
  const [loadingText, setLoadingText] = useState("Finding your records");

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
      columnFilters,
      globalFilter,
    },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
  });

  // Pass table instance to parent component if callback provided
  useEffect(() => {
    if (onTableReady) {
      onTableReady(table);
    }
  }, [table, onTableReady]);

  // Update state when initialState changes (for tab switching)
  useEffect(() => {
    if (initialState.rowSelection !== undefined) {
      setRowSelection(initialState.rowSelection);
    }
    if (initialState.columnVisibility !== undefined) {
      setColumnVisibility(initialState.columnVisibility);
    }
    if (initialState.columnFilters !== undefined) {
      setColumnFilters(initialState.columnFilters);
    }
    if (initialState.globalFilter !== undefined) {
      setGlobalFilter(initialState.globalFilter);
    }
    if (initialState.sorting !== undefined) {
      setSorting(initialState.sorting);
    }
  }, [initialState]);

  // Update loading text periodically
  useEffect(() => {
    if (loading) {
      const texts = [
        "Finding your records",
        `Pulling data for dates ${formatDate(dateRange[0])} and ${formatDate(
          dateRange[1]
        )}`,
        "Loading records",
      ];
      let index = 0;

      const interval = setInterval(() => {
        if (index < texts.length - 1) {
          setLoadingText(texts[index]);
          index++;
        } else {
          clearInterval(interval); // Stop updating after the last message
        }
      }, 2000); // Change text every 2 seconds

      return () => clearInterval(interval); // Cleanup interval
    }
  }, [loading, dateRange]);

  return (
    <div className="space-y-4">
      {!hideToolbar && <DataTableToolbar table={table} />}
      <div className="rounded-md border relative">
        {loading && (
          <div
            className="absolute inset-0 flex flex-col items-center justify-center bg-white bg-opacity-80 z-10"
            style={{ height: "300px" }}
          >
            <div className="loader animate-spin border-4 border-blue-500 border-t-transparent rounded-full h-12 w-12"></div>
            <p className="text-md font-medium">{loadingText}</p>
          </div>
        )}
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    colSpan={header.colSpan}
                    className="px-4 text-xs font-semibold uppercase tracking-wide text-zinc-500 align-top"
                  >
                    <div className="flex flex-col gap-1">
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                      {header.column.getCanFilter() && (
                        <input
                          type="text"
                          value={header.column.getFilterValue() || ""}
                          onChange={(e) =>
                            header.column.setFilterValue(e.target.value)
                          }
                          placeholder="Filter..."
                          className="mt-1 px-2 py-1 border border-gray-200 rounded-md text-xs w-full min-h-[28px] focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
                        />
                      )}
                    </div>
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {!loading && table.getRowModel().rows?.length ? (
              <>
                {table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    onClick={() => onRowClick(row.original.user_id)}
                    className="hover:bg-zinc-50"
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id} className="px-4 py-2.5">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
                {/* Add empty rows to maintain consistent height based on page size */}
                {Array.from({
                  length: Math.max(0, table.getState().pagination.pageSize - table.getRowModel().rows.length)
                }).map((_, index) => (
                  <TableRow key={`empty-${index}`} className="pointer-events-none">
                    {columns.length > 0 ? (
                      columns.map((_, colIndex) => (
                        <TableCell key={`empty-${index}-${colIndex}`} className="px-4 py-2.5">
                          &nbsp;
                        </TableCell>
                      ))
                    ) : (
                      <TableCell className="px-4 py-2.5">
                        &nbsp;
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </>
            ) : !loading ? (
              <>
                {/* First row shows the message */}
                <TableRow className="pointer-events-none">
                  <TableCell
                    colSpan={columns.length}
                    className="px-4 py-2.5 text-center text-gray-500"
                  >
                    No results. Try inputting different dates.
                  </TableCell>
                </TableRow>
                {/* Remaining empty rows to maintain consistent height */}
                {Array.from({
                  length: Math.max(0, (table.getState().pagination?.pageSize || 10) - 1)
                }).map((_, index) => (
                  <TableRow key={`empty-${index}`} className="pointer-events-none">
                    <TableCell colSpan={columns.length} className="px-4 py-2.5">
                      &nbsp;
                    </TableCell>
                  </TableRow>
                ))}
              </>
            ) : null}
          </TableBody>
        </Table>
      </div>
      {!loading && (
        <DataTablePagination table={table} />
      )}
    </div>
  );
}

export default DataTable;
